import { TranslateService } from '@ngx-translate/core';
import {
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ArrayUtils } from '@app/utilities/array.util';

import { ModalsService } from '@core/services/modals/modals.service';

import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

import { ShareUserFeedbackModalComponent } from '@shared/modals/share-user-feedback-modal/share-user-feedback-modal.component';
import { AnalyticsService } from '@core/services/app/analytics.service';
import {
  type UserFeedbackBlockConfig,
  type UserFeedbackBlockScenarioContent,
  userFeedbackBlockResolveConfig,
} from '@front-platform/content-builder';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { CB2_PREVIEW_BLOC_KEY } from '@core/services/cb2-preview/cb2-preview-bloc-key.token';
import { USER_FEEDBACK_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import {
  previewScenarioForBloc$,
  resolvePreviewScenarioForBloc,
} from '@core/services/cb2-preview/preview-scenario-for-bloc.util';

export interface UserFeedbackBlockRuntimeConfig {
  title?: string;
  positive_rating_threshold?: number;
  min_value_label?: string;
  max_value_label?: string;
  text_color?: IContentBuilderFieldColor;
  background_color?: IContentBuilderFieldColor;
  modal_positive_rating_title?: string;
  modal_positive_rating_content?: unknown[];
  modal_positive_rating_message?: string;
  modal_negative_rating_title?: string;
  modal_negative_rating_content?: unknown[];
  modal_negative_rating_message?: string;
  rating?: number;
  length?: number;
}

export type UserFeedbackBlockConfigInput = Partial<
  Omit<UserFeedbackBlockConfig, keyof UserFeedbackBlockRuntimeConfig> &
    UserFeedbackBlockRuntimeConfig
>;
export type UserFeedbackBlockResolvedConfig = Omit<
  UserFeedbackBlockConfig,
  keyof UserFeedbackBlockRuntimeConfig
> &
  Partial<UserFeedbackBlockRuntimeConfig>;
export interface UserFeedbackBlockContract extends ConfigComponentContract<
  UserFeedbackBlockConfigInput,
  UserFeedbackBlockResolvedConfig
> {}

@Component({
  selector: 'app-user-feedback-block',
  templateUrl: './user-feedback-block.component.html',
  styleUrls: ['./user-feedback-block.component.scss'],
  standalone: false,
})
export class UserFeedbackBlockComponent implements OnInit, UserFeedbackBlockContract {
  readonly config = input<UserFeedbackBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    UserFeedbackBlockConfig,
    UserFeedbackBlockRuntimeConfig
  >(userFeedbackBlockResolveConfig, () => ({ config: this.config() }));

  get title(): string | undefined {
    return this.resolvedConfig().title || 'user-feedback.title';
  }
  get positiveRatingThreshold(): number {
    return this.resolvedConfig().positive_rating_threshold ?? 3.5;
  }
  get minValueLabel(): string | undefined {
    return this.resolvedConfig().min_value_label || 'user-feedback.min-value-label';
  }
  get maxValueLabel(): string | undefined {
    return this.resolvedConfig().max_value_label || 'user-feedback.max-value-label';
  }
  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig().text_color as unknown as IContentBuilderFieldColor;
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig().background_color as unknown as IContentBuilderFieldColor;
  }
  get modalPositiveRatingTitle(): string | undefined {
    return this.resolvedConfig().modal_positive_rating_title || undefined;
  }
  get modalPositiveRatingContent(): unknown[] {
    return this.resolvedConfig().modal_positive_rating_content ?? [];
  }
  get modalPositiveRatingMessage(): string | undefined {
    return this.resolvedConfig().modal_positive_rating_message || undefined;
  }
  get modalNegativeRatingTitle(): string | undefined {
    return this.resolvedConfig().modal_negative_rating_title || undefined;
  }
  get modalNegativeRatingContent(): unknown[] {
    return this.resolvedConfig().modal_negative_rating_content ?? [];
  }
  get modalNegativeRatingMessage(): string | undefined {
    return this.resolvedConfig().modal_negative_rating_message || undefined;
  }

  private ratingOverride: number | undefined;
  get rating(): number | undefined {
    return this.ratingOverride ?? this.resolvedConfig().rating ?? 0;
  }
  set rating(value: number | undefined) {
    this.ratingOverride = value;
  }
  @Output() ratingChange = new EventEmitter<number>();

  get length(): number {
    return this.resolvedConfig().length ?? 5;
  }
  get items(): number[] {
    return ArrayUtils.from(this.length);
  }

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewBlocKey = inject(CB2_PREVIEW_BLOC_KEY, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly previewScenario$ = previewScenarioForBloc$(USER_FEEDBACK_BLOCK_COMPONENT);

  constructor(
    private modalService: ModalsService,
    private translator: TranslateService,
    private analyticsService: AnalyticsService,
  ) {
    this.analyticsService.trackEvent('user_feedback_display');

    effect(() => {
      if (!this.cb2PreviewState.isActive()) {
        return;
      }
      this.resolvedConfig();
      const content = previewScenarioData<UserFeedbackBlockScenarioContent>(
        resolvePreviewScenarioForBloc(
          this.cb2PreviewState.previewScenarios(),
          this.previewBlocKey,
          USER_FEEDBACK_BLOCK_COMPONENT,
        ),
        USER_FEEDBACK_BLOCK_COMPONENT,
      );
      if (content?.phase !== 'positive' && content?.phase !== 'negative') {
        return;
      }
      untracked(() =>
        this.publishShareFeedbackModal(content.phase === 'positive'),
      );
    });
  }

  ngOnInit(): void {
    if (!this.cb2PreviewState.isActive()) {
      return;
    }

    this.previewScenario$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(scenario => {
      const content = previewScenarioData<UserFeedbackBlockScenarioContent>(
        scenario,
        USER_FEEDBACK_BLOCK_COMPONENT,
      );
      this.applyPreviewScenario(content);
    });
  }

  onRated(_event: Event, _value: number): void {
    if (this.cb2PreviewState.isActive()) {
      return;
    }
    this._setRating(_value);
    const _isPositive = _value >= Number(this.positiveRatingThreshold || 3.5);
    setTimeout(() => {
      this._openSuggestShareFeedbackModal(_isPositive);
    }, 200);
    this.analyticsService.trackEvent('user_feedback_rate', {
      user_feedback_rating: _value,
      user_feedback_is_positive: _isPositive,
    });
  }

  private _setRating(_value: number): void {
    this.rating = _value;
    this.ratingChange.emit(this.rating);
  }

  private _openSuggestShareFeedbackModal(_isPositive: boolean): void {
    this.modalService.open(
      ShareUserFeedbackModalComponent.handle,
      undefined,
      this.buildShareFeedbackModalData(_isPositive),
    );
  }

  private buildShareFeedbackModalData(isPositive: boolean): {
    title: string;
    description: string;
    items: unknown[];
  } {
    return {
      title: isPositive
        ? this.translator.instant(
            this.modalPositiveRatingTitle || 'user-feedback.modal.positive.title',
          )
        : this.translator.instant(
            this.modalNegativeRatingTitle || 'user-feedback.modal.negative.title',
          ),
      description: isPositive
        ? this.translator.instant(
            this.modalPositiveRatingMessage || 'user-feedback.modal.positive.rating-message',
          )
        : this.translator.instant(
            this.modalNegativeRatingMessage || 'user-feedback.modal.negative.rating-message',
          ),
      items: isPositive ? this.modalPositiveRatingContent : this.modalNegativeRatingContent,
    };
  }

  private applyPreviewScenario(content: UserFeedbackBlockScenarioContent | null): void {
    if (!content || content.phase === 'initial') {
      this.modalService.close(ShareUserFeedbackModalComponent.handle);
      this.rating = 0;
      return;
    }

    const isPositive = content.phase === 'positive';
    this.rating = isPositive ? this.length : 1;
    this.publishShareFeedbackModal(isPositive);
  }

  private publishShareFeedbackModal(isPositive: boolean): void {
    const data = this.buildShareFeedbackModalData(isPositive);
    this.modalService.open(ShareUserFeedbackModalComponent.handle, undefined, data);
    this.modalService.setData(ShareUserFeedbackModalComponent.handle, data);
  }
}

COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(UserFeedbackBlockComponent, {});
